import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { BackIcon } from '../components/Icons';
import { api, type FamilyProfile, type FamilyProfileEdit } from '../lib/api';

/**
 * Edit your own profile.
 *
 * Everything here is stored in Nextcloud, not in this app, so a name or photo
 * set here is the same one Talk and the Nextcloud web UI show. That is also
 * why saving refreshes the auth context: the header, feed and chat all read
 * the display name from there.
 */

interface FieldDefinition {
  key: keyof FamilyProfileEdit;
  label: string;
  hint?: string;
  placeholder?: string;
  multiline?: boolean;
  type?: string;
  maxLength: number;
}

/**
 * Ordered by how much they help somebody recognise a relative, not by how
 * much Nextcloud can store. Phone and address are last because they are the
 * ones people are most hesitant to fill in.
 */
const FIELDS: FieldDefinition[] = [
  { key: 'headline', label: 'About you', hint: 'A short line under your name.', placeholder: 'Grandma of six', maxLength: 255 },
  { key: 'pronouns', label: 'Pronouns', placeholder: 'she/her', maxLength: 64 },
  { key: 'biography', label: 'More about you', multiline: true, placeholder: 'Anything you would like the family to know.', maxLength: 1024 },
  { key: 'role', label: 'Family role', hint: 'How the little ones know you.', placeholder: 'Oma', maxLength: 255 },
  { key: 'organisation', label: 'Where you work', placeholder: 'Retired', maxLength: 255 },
  { key: 'email', label: 'Email', type: 'email', hint: 'Used for account emails from the family cloud.', maxLength: 254 },
  { key: 'phone', label: 'Phone', type: 'tel', hint: 'Include the country code, like +1 801 555 0100.', maxLength: 32 },
  { key: 'address', label: 'Where you live', placeholder: 'Salt Lake City, Utah', maxLength: 255 },
];

export function ProfilePage() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();

  const [profile, setProfile] = useState<FamilyProfile | null>(null);
  const [draft, setDraft] = useState<FamilyProfileEdit>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Bumping this re-requests the avatar, which the browser would otherwise
  // serve from cache and show the previous photo.
  const [avatarVersion, setAvatarVersion] = useState(() => user?.avatarVersion ?? 1);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const [hasCustomAvatar, setHasCustomAvatar] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const load = async () => {
    setLoading(true);
    try {
      const result = await api.profile();
      setProfile(result.profile);
      setDraft({});
      setError(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not load your profile.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  // Whether a real photo is set decides if "Remove photo" is offered at all.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    void fetch(api.avatarUrl(user.id, 64, avatarVersion), { credentials: 'include' })
      .then((response) => {
        if (!cancelled) setHasCustomAvatar(response.headers.get('x-custom-avatar') === '1');
      })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, [user, avatarVersion]);

  const value = (key: keyof FamilyProfileEdit): string => (
    draft[key] ?? profile?.[key] ?? ''
  );

  const setValue = (key: keyof FamilyProfileEdit, next: string) => {
    setDraft((previous) => ({ ...previous, [key]: next }));
    setStatus(null);
  };

  const displayNameValue = draft.displayName ?? profile?.displayName ?? '';
  const nameIsEmpty = displayNameValue.trim().length === 0;
  const dirty = Object.keys(draft).length > 0;

  const save = async () => {
    if (!dirty || saving || nameIsEmpty) return;
    setSaving(true);
    setError(null);
    setStatus(null);
    setFieldErrors({});
    try {
      // Nextcloud stores the name under a different key than the app uses.
      const { displayName, ...rest } = draft;
      const payload: Record<string, string> = { ...rest as Record<string, string> };
      if (displayName !== undefined) payload.displayname = displayName;

      const result = await api.saveProfile(payload as FamilyProfileEdit);
      setProfile(result.profile);
      setDraft({});
      setFieldErrors(result.fieldErrors ?? {});
      setStatus(result.fieldErrors ? 'Saved, apart from the details noted below.' : 'Saved.');
      // The name appears all over the app, so refresh the shared copy.
      await refresh();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not save your profile.');
    } finally {
      setSaving(false);
    }
  };

  const choosePhoto = () => fileInput.current?.click();

  const onPhotoChosen = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Clear immediately so choosing the same file twice still fires a change.
    event.target.value = '';
    if (!file) return;

    setAvatarBusy(true);
    setError(null);
    setStatus(null);
    try {
      const result = await api.uploadAvatar(file);
      setAvatarVersion(result.avatarVersion);
      // Other screens read the version from the auth context.
      await refresh();
      setStatus('Photo updated.');
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'That photo could not be saved.');
    } finally {
      setAvatarBusy(false);
    }
  };

  const removePhoto = async () => {
    if (avatarBusy) return;
    setAvatarBusy(true);
    setError(null);
    setStatus(null);
    try {
      const result = await api.removeAvatar();
      setAvatarVersion(result.avatarVersion);
      await refresh();
      setStatus('Photo removed.');
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not remove your photo.');
    } finally {
      setAvatarBusy(false);
    }
  };

  const initial = (displayNameValue || user?.displayName || '?').slice(0, 1).toUpperCase();

  return (
    <>
      <header className="app-header profile-header">
        <button
          type="button"
          className="chat-back"
          onClick={() => navigate('/settings')}
          aria-label="Back to settings"
        >
          <BackIcon />
        </button>
        <h1>Your profile</h1>
      </header>

      <div className="page">
        {loading ? (
          <div className="center-fill">
            <div className="spinner" role="status" aria-label="Loading your profile" />
          </div>
        ) : (
          <>
            {error && (
              <div className="banner error" role="alert">
                <p>{error}</p>
              </div>
            )}
            {status && (
              <div className="banner profile-status" role="status">
                <p>{status}</p>
              </div>
            )}

            <div className="card profile-photo-card">
              <div className="profile-avatar-wrap">
                {user && (
                  <img
                    className="profile-avatar"
                    src={api.avatarUrl(user.id, 256, avatarVersion)}
                    alt=""
                    onError={(event) => { event.currentTarget.style.display = 'none'; }}
                  />
                )}
                <span className="profile-avatar-fallback" aria-hidden="true">{initial}</span>
              </div>
              <div className="profile-photo-actions">
                <h2>Profile photo</h2>
                <p>Family see this next to your messages and photos.</p>
                <div className="profile-photo-buttons">
                  <button type="button" className="btn" onClick={choosePhoto} disabled={avatarBusy}>
                    {avatarBusy ? 'Working…' : hasCustomAvatar ? 'Change photo' : 'Add photo'}
                  </button>
                  {hasCustomAvatar && (
                    <button type="button" className="text-button" onClick={() => void removePhoto()} disabled={avatarBusy}>
                      Remove
                    </button>
                  )}
                </div>
                <input
                  ref={fileInput}
                  type="file"
                  accept="image/jpeg,image/png"
                  hidden
                  onChange={(event) => void onPhotoChosen(event)}
                />
              </div>
            </div>

            <div className="card profile-form">
              <h2>Your name</h2>
              <label className="field-label" htmlFor="profile-displayname">
                Name family see
              </label>
              <input
                id="profile-displayname"
                value={displayNameValue}
                maxLength={64}
                disabled={saving}
                aria-invalid={nameIsEmpty || Boolean(fieldErrors.displayname)}
                onChange={(event) => setValue('displayName', event.target.value)}
              />
              {nameIsEmpty && <p className="field-error">Your name cannot be empty.</p>}
              {fieldErrors.displayname && <p className="field-error">{fieldErrors.displayname}</p>}

              {FIELDS.map((field) => (
                <div key={field.key}>
                  <label className="field-label" htmlFor={`profile-${field.key}`}>{field.label}</label>
                  {field.multiline ? (
                    <textarea
                      id={`profile-${field.key}`}
                      value={value(field.key)}
                      rows={3}
                      maxLength={field.maxLength}
                      disabled={saving}
                      placeholder={field.placeholder}
                      onChange={(event) => setValue(field.key, event.target.value)}
                    />
                  ) : (
                    <input
                      id={`profile-${field.key}`}
                      type={field.type ?? 'text'}
                      value={value(field.key)}
                      maxLength={field.maxLength}
                      disabled={saving}
                      placeholder={field.placeholder}
                      aria-invalid={Boolean(fieldErrors[field.key])}
                      onChange={(event) => setValue(field.key, event.target.value)}
                    />
                  )}
                  {fieldErrors[field.key]
                    ? <p className="field-error">{fieldErrors[field.key]}</p>
                    : field.hint && <p className="field-hint">{field.hint}</p>}
                </div>
              ))}

              <button
                type="button"
                className="btn btn-primary profile-save"
                onClick={() => void save()}
                disabled={!dirty || saving || nameIsEmpty}
                aria-busy={saving}
              >
                {saving ? 'Saving…' : 'Save profile'}
              </button>
              <p className="profile-note">
                Your name and details are stored in the family cloud, so they show up
                in chat and photos too.
              </p>
            </div>
          </>
        )}
      </div>
    </>
  );
}
